import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { NavMarker } from 'react-rhumb';
import UILoadingSpinner from 'UIComponents/loading/UILoadingSpinner';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { useSetErrorAfterTimeout } from '../hooks/useSetErrorAfterTimeout';
import { getActiveLoading, getCanSwitchCopy } from '../store/loading-status/selectors';
import { LoadingCentered, LoadingIllustration, LoadingImage } from './Loading.styles';
import { LoadingTitle } from './LoadingTitle';
import { useCopyCarousel } from './useCopyCarousel';
import { useLoadingTitle } from './useLoadingTitle'; // @ts-expect-error dependency missing types

import Confetti from 'growth-onboarding-shared-demo-components/transitions/Confetti';

const BaseLoadingView = ({
  loadingKey,
  hasSpinner = true,
  imageUrl,
  imageClass,
  textDisplayDelay = 0,
  isIllustration,
  hasConfetti = false,
  illustrationHeight
}) => {
  const {
    titleI18n,
    subtitleI18n
  } = useLoadingTitle({
    loadingKey
  });
  const canSwitchCopy = useSelector(getCanSwitchCopy(loadingKey));
  const {
    copyToDisplay: titleToDisplay
  } = useCopyCarousel({
    baseI18n: titleI18n,
    loadingKey,
    canSwitchCopy,
    textDisplayDelay
  });
  const [imageLoaded, setImageLoaded] = useState(false);
  const [showSpinner, setShouldShowSpinner] = useState(true);
  const [shouldShowText, setShouldShowText] = useState(false);
  const shouldNotAnimate = Boolean(useSelector(getActiveLoading));
  useSetErrorAfterTimeout(loadingKey);

  const renderSpinner = () => {
    return (hasSpinner || !imageLoaded && showSpinner) && /*#__PURE__*/_jsx(UILoadingSpinner, {
      grow: true,
      minHeight: 100,
      size: "md"
    });
  };

  const renderImage = () => {
    if (isIllustration && imageUrl) {
      return /*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(LoadingIllustration, {
          className: imageClass,
          height: illustrationHeight,
          name: imageUrl,
          onLoad: () => {
            setImageLoaded(true);
            setShouldShowSpinner(false);
          }
        })
      });
    }

    return imageUrl && /*#__PURE__*/_jsx("div", {
      style: {
        display: imageLoaded ? 'block' : 'none'
      },
      children: /*#__PURE__*/_jsx(LoadingImage, {
        className: imageClass,
        src: imageUrl,
        onLoad: () => {
          setImageLoaded(true);
          setShouldShowSpinner(false);
        }
      })
    });
  };

  const renderText = () => {
    if ((imageUrl && imageLoaded || showSpinner) && shouldShowText) {
      return /*#__PURE__*/_jsxs("div", {
        children: [isValidI18nKey(titleToDisplay) && /*#__PURE__*/_jsx(LoadingTitle, {
          title: titleToDisplay
        }), isValidI18nKey(subtitleI18n) && /*#__PURE__*/_jsx(FormattedMessage, {
          message: subtitleI18n
        })]
      });
    }

    return null;
  };

  useEffect(() => {
    const timeout = setTimeout(() => setShouldShowText(true), textDisplayDelay);
    return () => {
      clearTimeout(timeout);
    };
  }, [shouldShowText, textDisplayDelay]);
  return /*#__PURE__*/_jsxs(AnimatedStepBody, {
    disableAnimation: shouldNotAnimate,
    children: [hasConfetti && /*#__PURE__*/_jsx(Confetti, {}), /*#__PURE__*/_jsx(NavMarker, {
      name: "LOADING"
    }), /*#__PURE__*/_jsx("div", {
      "data-selenium": loadingKey,
      children: /*#__PURE__*/_jsxs(LoadingCentered, {
        children: [renderSpinner(), renderImage(), renderText()]
      })
    })]
  });
};

export default BaseLoadingView;